/**
 * Everything that differs between an expense entry and an income entry.
 *
 * Income and expenses share one data shape and one set of components, so all
 * the variation — copy, behaviour flags, colour — is collected here rather
 * than scattered across conditionals in each component.
 *
 * IMPORTANT: every Tailwind class below is a complete literal, matching the
 * rule documented in ThemeContext.js. Tailwind generates CSS by scanning source
 * files for class-shaped text, so interpolating a colour or a variant prefix
 * here (`bg-${c}-500`, `hover:${g}`) would produce a class the scanner never
 * sees. Joining whole tokens at a call site — `"bg-gradient-to-r"` plus
 * `"from-emerald-500 to-teal-500"` — is fine; building a token out of fragments
 * is not.
 */

/**
 * @typedef {Object} EntryKind
 * @property {'expense'|'income'} key
 * @property {boolean} hasEarnedDate Carries a second, earned-on date.
 * @property {boolean} supportsOutstanding Can exist before the cash arrives.
 * @property {Object|null} accent Complete class literals, or null to follow the
 *   user's selected theme.
 */

/** @type {EntryKind} */
export const EXPENSE_KIND = {
  key: 'expense',
  title: 'Expenses',
  addLabel: 'Add Expense',
  addingLabel: 'Adding...',
  peekLabel: '+ Add expense',
  successText: 'Expense added.',
  failureText: "Couldn't add that expense. Please try again.",
  missingCategoryText: 'Pick a category first.',
  categoryLabel: 'Category',
  categoryPlural: 'Categories',
  newCategoryPlaceholder: 'New category name',
  noCategoriesHint: 'Add a category in Settings → Expenses first.',
  emptyListText: 'No expenses yet this month',
  emptyBreakdownText: 'Nothing spent this month yet',
  saveErrorText: "Couldn't save that change. Please try again.",
  deleteErrorText: "Couldn't delete that expense. Please try again.",
  loadErrorText: "Couldn't load expenses for that month.",
  hasEarnedDate: false,
  supportsOutstanding: false,
  // null = follow the user's selected theme, preserving today's behaviour
  // exactly. Resolved by useEntryAccent.
  accent: null,
};

/** @type {EntryKind} */
export const INCOME_KIND = {
  key: 'income',
  title: 'Income',
  addLabel: 'Add Income',
  addingLabel: 'Adding...',
  peekLabel: '+ Add income',
  successText: 'Income added.',
  failureText: "Couldn't add that income. Please try again.",
  missingCategoryText: 'Pick a source first.',
  // Income comes from a source, not a category. Worth the extra field: "Add a
  // category" reads wrong next to a list of employers and clients.
  categoryLabel: 'Source',
  categoryPlural: 'Sources',
  newCategoryPlaceholder: 'New income source',
  noCategoriesHint: 'Add a source in Settings → Income first.',
  emptyListText: 'No income yet this month',
  emptyBreakdownText: 'Nothing received this month yet',
  saveErrorText: "Couldn't save that change. Please try again.",
  deleteErrorText: "Couldn't delete that income. Please try again.",
  loadErrorText: "Couldn't load income for that month.",
  hasEarnedDate: true,
  supportsOutstanding: true,
  // Income is always green, independent of the selected theme. Encoding six
  // per-theme income variants in ThemeContext would be 30+ extra literals for
  // no user-visible gain, and green-for-money-in is a strong enough convention
  // to be worth breaking theme consistency for.
  accent: {
    button: 'from-emerald-500 to-teal-500',
    buttonHover: 'hover:from-emerald-600 hover:to-teal-600',
    shadow: 'shadow-emerald-200',
    chip: 'bg-emerald-50 text-emerald-700',
    heroBg: 'from-emerald-600 via-emerald-700 to-teal-700',
    textMuted: 'text-emerald-200',
  },
};

/** Lookup by key, for the Home tab's Income|Expense toggle. */
export const ENTRY_KINDS = {
  [EXPENSE_KIND.key]: EXPENSE_KIND,
  [INCOME_KIND.key]: INCOME_KIND,
};
